import React, { useState, useCallback } from 'react';
import { Activity, RefreshCw, AlertTriangle, CheckCircle, XCircle, TrendingDown, Search, ChevronDown, ChevronUp } from 'lucide-react';

const WORKER_URL = 'https://tickertapapi.avetter90.workers.dev';
const DEFAULT_WATCHLIST = ['SLV', 'IBIT', 'UVXY', 'SPY', 'JEPQ', 'GLD'];

function ScoreBadge({ score }) {
  const color = score >= 70 ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
    : score >= 45 ? 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20'
    : 'text-red-400 bg-red-500/10 border-red-500/20';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-bold ${color}`}>{score}/100</span>;
}

function SignalIcon({ pass }) {
  if (pass === true) return <CheckCircle className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />;
  if (pass === false) return <XCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />;
  return <AlertTriangle className="w-3.5 h-3.5 text-yellow-400 flex-shrink-0" />;
}

function StatCell({ label, value, color = 'text-white' }) {
  return (
    <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded p-3">
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className={`text-lg font-bold ${color}`}>{value ?? '—'}</div>
    </div>
  );
}

function AuthGate({ onAuthorize }) {
  return (
    <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg p-10 text-center max-w-md mx-auto mt-16">
      <TrendingDown className="w-12 h-12 text-gray-600 mx-auto mb-4" />
      <h3 className="text-lg font-bold text-white mb-2">Connect Tastytrade</h3>
      <p className="text-sm text-gray-400 mb-6">
        Authorize read-only access to pull live options chains, IV rank, and greeks for the edge scanner.
      </p>
      <button onClick={onAuthorize} className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-semibold transition-colors">
        Authorize with Tastytrade
      </button>
    </div>
  );
}

function EdgeDetail({ data, onClose }) {
  if (!data) return null;
  return (
    <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg p-6 mt-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="font-mono font-bold text-white text-xl">{data.symbol}</span>
          <ScoreBadge score={data.score} />
          <span className={`text-sm font-semibold ${data.score >= 70 ? 'text-emerald-400' : data.score >= 45 ? 'text-yellow-400' : 'text-red-400'}`}>{data.verdict}</span>
        </div>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-300 text-xl leading-none">×</button>
      </div>
      <div className="grid grid-cols-4 gap-3 mb-4">
        <StatCell label="IV rank" value={`${data.ivRank}%`} color={data.ivRank >= 50 ? 'text-emerald-400' : 'text-yellow-400'} />
        <StatCell label="Current IV" value={`${data.currentIV}%`} />
        <StatCell label="HV 20-day" value={`${data.hv20}%`} />
        <StatCell label="HV 60-day" value={`${data.hv60}%`} />
        <StatCell label="IV premium" value={`${data.ivPremium > 0 ? '+' : ''}${data.ivPremium} pts`} color={data.ivPremium >= 5 ? 'text-emerald-400' : 'text-yellow-400'} />
        <StatCell label="ATM IV" value={`${data.atmIV}%`} />
        <StatCell label="25d put IV" value={`${data.otmPutIV}%`} />
        <StatCell label="25d call IV" value={`${data.otmCallIV}%`} />
        <StatCell label="Risk reversal" value={`${data.riskReversal > 0 ? '+' : ''}${data.riskReversal} pts`} />
        <StatCell label="Skew type" value={data.skewType} />
        <StatCell label="DTE" value={`${data.dte}d`} color={data.dte >= 28 && data.dte <= 45 ? 'text-emerald-400' : 'text-yellow-400'} />
        <StatCell label="Expiry" value={data.expiry} />
      </div>
      <div className="mb-4">
        <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Signal checklist</div>
        <div className="space-y-1.5">
          {(data.signals || []).map((s, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <SignalIcon pass={s.pass} />
              <span className={s.pass === true ? 'text-gray-300' : s.pass === false ? 'text-red-400' : 'text-yellow-400'}>{s.label}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded p-4">
        <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Recommended strategy</div>
        <div className="text-sm font-semibold text-white mb-1">{data.strategy}</div>
        <div className="text-xs text-gray-400 leading-relaxed">{data.strategyDetail}</div>
      </div>
      {data.timestamp && <div className="text-xs text-gray-600 mt-3 text-right">Scored {new Date(data.timestamp).toLocaleTimeString()}</div>}
    </div>
  );
}

export default function EdgeTerminal() {
  const [authorized, setAuthorized] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [watchlist, setWatchlist] = useState(DEFAULT_WATCHLIST);
  const [addSymbol, setAddSymbol] = useState('');
  const [threshold, setThreshold] = useState(50);
  const [error, setError] = useState(null);

  React.useEffect(() => { checkAuth(); }, []);

  async function checkAuth() {
    try {
      const text = await fetch(`${WORKER_URL}/debug-tasty`).then(r => r.text());
      setAuthorized(text.includes('YES'));
    } catch { setAuthorized(false); }
  }

  async function handleAuthorize() {
    // /tasty/auth does a 302 redirect directly to Tastytrade login page
    window.open(`${WORKER_URL}/tasty/auth`, '_blank', 'width=600,height=700');
    // Poll until token stored
    const poll = setInterval(async () => {
      try {
        const text = await fetch(`${WORKER_URL}/debug-tasty`).then(r => r.text());
        if (text.includes('YES')) { clearInterval(poll); setAuthorized(true); }
      } catch {}
    }, 3000);
    setTimeout(() => clearInterval(poll), 120000);
  }

  async function runScan() {
    setScanning(true);
    setError(null);
    setScanResults(null);
    setSelectedSymbol(null);
    setSelectedData(null);
    try {
      const data = await fetch(`${WORKER_URL}/tasty/scan?symbols=${watchlist.join(',')}&threshold=${threshold}`).then(r => r.json());
      if (data.error) { if (data.auth_url) setAuthorized(false); else throw new Error(data.error); return; }
      setScanResults(data);
    } catch (e) { setError('Scan failed: ' + e.message); }
    finally { setScanning(false); }
  }

  async function loadSymbolDetail(symbol, preloaded) {
    if (selectedSymbol === symbol) { setSelectedSymbol(null); setSelectedData(null); return; }
    setSelectedSymbol(symbol);
    if (preloaded) { setSelectedData(preloaded); return; }
    try {
      const data = await fetch(`${WORKER_URL}/tasty/edge?symbol=${symbol}`).then(r => r.json());
      if (data.error && data.auth_url) { setAuthorized(false); return; }
      setSelectedData(data);
    } catch (e) { setError('Failed to load ' + symbol + ': ' + e.message); }
  }

  function addToWatchlist() {
    const sym = addSymbol.trim().toUpperCase();
    if (sym && !watchlist.includes(sym)) setWatchlist(prev => [...prev, sym]);
    setAddSymbol('');
  }

  if (authorized === null) {
    return (
      <div className="max-w-[1200px] mx-auto p-8">
        <div className="flex items-center gap-3 text-gray-400">
          <Activity className="w-4 h-4 animate-spin" />
          <span className="text-sm">Checking Tastytrade connection...</span>
        </div>
      </div>
    );
  }

  if (authorized === false) {
    return (
      <div className="max-w-[1200px] mx-auto p-8">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-white">Edge Terminal</h2>
          <p className="text-xs text-gray-500 mt-1">Options selling edge scanner — McMillan framework</p>
        </div>
        <AuthGate onAuthorize={handleAuthorize} />
      </div>
    );
  }

  return (
    <div className="max-w-[1200px] mx-auto p-8">

      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white">Edge Terminal</h2>
          <p className="text-xs text-gray-500 mt-1">Options selling edge scanner — McMillan framework</p>
        </div>
        <span className="flex items-center gap-1.5 text-xs text-emerald-400">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
          Tastytrade connected
        </span>
      </div>

      <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg p-5 mb-4">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs text-gray-500 uppercase tracking-wider">Watchlist</div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500">Min score</span>
            <input type="number" value={threshold} onChange={e => setThreshold(parseInt(e.target.value) || 0)} min={0} max={100}
              className="w-16 bg-[#1a1a1a] border border-[#2a2a2a] rounded px-2 py-1 text-white text-xs text-center focus:outline-none" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {watchlist.map(sym => (
            <span key={sym} className="flex items-center gap-1 bg-[#1a1a1a] border border-[#2a2a2a] rounded px-2.5 py-1 text-xs font-mono font-bold text-white">
              {sym}
              <button onClick={() => setWatchlist(prev => prev.filter(s => s !== sym))} className="text-gray-600 hover:text-red-400 ml-1 transition-colors">×</button>
            </span>
          ))}
          <div className="flex items-center gap-1">
            <input type="text" value={addSymbol} onChange={e => setAddSymbol(e.target.value.toUpperCase())} onKeyDown={e => e.key === 'Enter' && addToWatchlist()}
              placeholder="Add ticker" maxLength={6} className="w-24 bg-[#1a1a1a] border border-[#2a2a2a] rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-gray-500 font-mono" />
            <button onClick={addToWatchlist} className="text-gray-400 hover:text-white text-lg leading-none px-1">+</button>
          </div>
        </div>
        <button onClick={runScan} disabled={scanning}
          className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded text-sm font-semibold transition-colors">
          {scanning ? <><Activity className="w-4 h-4 animate-spin" />Scanning {watchlist.length} symbols...</> : <><Search className="w-4 h-4" />Run scan</>}
        </button>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4 text-sm text-red-400">{error}</div>}

      {scanResults && (
        <>
          <div className="flex items-center gap-4 mb-3">
            <span className="text-xs text-gray-500">{scanResults.scanned} symbols scanned</span>
            <span className="text-xs text-emerald-400">{scanResults.hits?.length || 0} above threshold ({threshold})</span>
            {scanResults.timestamp && <span className="text-xs text-gray-600 ml-auto">{new Date(scanResults.timestamp).toLocaleTimeString()}</span>}
          </div>
          <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg overflow-hidden mb-4">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#1a1a1a]">
                  {['Symbol', 'Score', 'Verdict', 'IV rank', 'IV premium', 'Skew', 'DTE', 'Strategy', ''].map((h, i) => (
                    <th key={h} className={`${i === 0 ? 'text-left' : i === 8 ? 'text-center' : 'text-right'} px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(scanResults.all || []).sort((a, b) => b.score - a.score).map(row => {
                  const isSelected = selectedSymbol === row.symbol;
                  const isHit = row.score >= threshold;
                  return (
                    <React.Fragment key={row.symbol}>
                      <tr onClick={() => loadSymbolDetail(row.symbol, row)}
                        className={`border-b border-[#1a1a1a] cursor-pointer transition-colors ${isSelected ? 'bg-white/5' : 'hover:bg-[#141414]'} ${row.error ? 'opacity-50' : ''}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isHit ? 'bg-emerald-400' : 'bg-gray-600'}`} />
                            <span className="font-mono font-bold text-white">{row.symbol}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">{row.error ? <span className="text-xs text-red-400">Error</span> : <ScoreBadge score={row.score} />}</td>
                        <td className="px-4 py-3 text-right"><span className={`text-xs font-semibold ${row.score >= 70 ? 'text-emerald-400' : row.score >= 45 ? 'text-yellow-400' : 'text-gray-500'}`}>{row.verdict}</span></td>
                        <td className={`px-4 py-3 text-right text-sm font-semibold ${row.ivRank >= 50 ? 'text-emerald-400' : 'text-gray-400'}`}>{row.error ? '—' : `${row.ivRank}%`}</td>
                        <td className={`px-4 py-3 text-right text-sm ${row.ivPremium >= 5 ? 'text-emerald-400' : row.ivPremium >= 0 ? 'text-yellow-400' : 'text-red-400'}`}>{row.error ? '—' : `${row.ivPremium > 0 ? '+' : ''}${row.ivPremium} pts`}</td>
                        <td className="px-4 py-3 text-right text-xs text-gray-400">{row.error ? '—' : row.skewType}</td>
                        <td className={`px-4 py-3 text-right text-sm ${row.dte >= 28 && row.dte <= 45 ? 'text-emerald-400' : 'text-gray-400'}`}>{row.error ? '—' : `${row.dte}d`}</td>
                        <td className="px-4 py-3 text-right text-xs text-gray-400 max-w-[160px] truncate">{row.error ? row.error : row.strategy}</td>
                        <td className="px-4 py-3 text-center">{isSelected ? <ChevronUp className="w-4 h-4 text-gray-400 mx-auto" /> : <ChevronDown className="w-4 h-4 text-gray-500 mx-auto" />}</td>
                      </tr>
                      {isSelected && (
                        <tr className="border-b border-[#1a1a1a]">
                          <td colSpan={9} className="p-0">
                            <div className="p-4 bg-[#0a0a0a]">
                              <EdgeDetail data={selectedData} onClose={() => { setSelectedSymbol(null); setSelectedData(null); }} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {(scanResults.hits?.length || 0) === 0 && (
            <div className="text-center py-8 text-gray-600 text-sm">No symbols above threshold {threshold}. Lower the threshold or wait for IV expansion.</div>
          )}
        </>
      )}

      {!scanResults && !scanning && (
        <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg p-12 text-center">
          <Search className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-white mb-2">Ready to scan</h3>
          <p className="text-sm text-gray-500">Hit "Run scan" to score your watchlist against the McMillan framework.</p>
          <p className="text-xs text-gray-600 mt-2">Pulls live IV rank, greeks, and options chain from Tastytrade. Scores each symbol across 5 signals.</p>
        </div>
      )}

    </div>
  );
}
